import type { NavIconId } from './NavIcons';

export type NavItem = {
    to: string;
    /** Full label (desktop nav + accessibility). */
    label: string;
    /** Short label for mobile dock & drawer under icon. */
    shortLabel: string;
    icon: NavIconId;
};

export const adminNavLinks: NavItem[] = [
    { to: '/admin/dashboard', label: 'Dashboard', shortLabel: 'Home', icon: 'dashboard' },
    { to: '/admin/clients', label: 'Clients', shortLabel: 'Clients', icon: 'clients' },
    { to: '/admin/work-orders', label: 'Jobs', shortLabel: 'Jobs', icon: 'jobs' },
    { to: '/admin/work-order-forms', label: 'WO Forms', shortLabel: 'Forms', icon: 'reports' },
    { to: '/admin/inspections', label: 'Inspections', shortLabel: 'Inspect', icon: 'inspections' },
    { to: '/admin/quotes', label: 'Quotes', shortLabel: 'Quotes', icon: 'quotes' },
    { to: '/admin/invoices', label: 'Invoices', shortLabel: 'Bills', icon: 'invoices' },
    { to: '/admin/deficiencies', label: 'Deficiencies', shortLabel: 'Issues', icon: 'deficiencies' },
    { to: '/admin/settings', label: 'Settings', shortLabel: 'Settings', icon: 'settings' }
];

/** Primary bottom dock tabs (3) + More = 4 sections total. */
export const adminMobileDockTabs: NavItem[] = [
    adminNavLinks[0],
    adminNavLinks[2],
    adminNavLinks[1]
];

export const adminMobileMoreLinks: NavItem[] = [
    adminNavLinks[3],
    adminNavLinks[4],
    adminNavLinks[5],
    adminNavLinks[6],
    adminNavLinks[7],
    adminNavLinks[8]
];

export const technicianNavLinks: NavItem[] = [
    { to: '/technician/dashboard', label: 'Dashboard', shortLabel: 'Home', icon: 'dashboard' },
    { to: '/technician/JobList', label: 'Job List', shortLabel: 'Jobs', icon: 'jobList' },
    { to: '/technician/JobLog', label: 'Job Log', shortLabel: 'Log', icon: 'jobLog' },
    { to: '/technician/TimeSheet', label: 'Time Sheet', shortLabel: 'Time', icon: 'timeSheet' },
    { to: '/technician/ExportReport', label: 'Export Report', shortLabel: 'Export', icon: 'reports' },
    { to: '/technician/settings', label: 'Settings', shortLabel: 'Settings', icon: 'settings' }
];

export const technicianMobileDockTabs: NavItem[] = [
    technicianNavLinks[0],
    technicianNavLinks[1],
    technicianNavLinks[3]
];

export const technicianMobileMoreLinks: NavItem[] = [
    technicianNavLinks[2],
    technicianNavLinks[4],
    technicianNavLinks[5]
];

/** Admin settings (and linked tools) stay available while an admin is in field mode. */
export const ADMIN_SETTINGS_PATHS = ['/admin/settings', '/admin/pdf-studio'] as const;

export function isAdminSettingsPath(pathname: string): boolean {
    return ADMIN_SETTINGS_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** Technician nav + dock when the user is a tech, or an admin with field mode on. */
export function isTechnicianShellActive(role: string | undefined, fieldMode: boolean): boolean {
    const r = typeof role === 'string' ? role.trim().toLowerCase() : '';
    if (r === 'technician') return true;
    if (r === 'admin' && fieldMode) return true;
    return false;
}

/** Nav items for the current shell. Admins in field mode use tech nav; Settings → admin settings. */
export function getNavLinks(role: string | undefined, fieldMode: boolean): NavItem[] {
    if (!isTechnicianShellActive(role, fieldMode)) {
        return adminNavLinks;
    }
    if (typeof role === 'string' && role.trim().toLowerCase() === 'admin' && fieldMode) {
        return technicianNavLinks.map((item) =>
            item.to === '/technician/settings'
                ? { ...item, to: '/admin/settings', label: 'Settings', shortLabel: 'Settings' }
                : item
        );
    }
    return technicianNavLinks;
}

export type MobileDockConfig = {
    tabs: NavItem[];
    moreLinks: NavItem[];
};

export function resolveMobileDock(
    pathname: string,
    role: string | undefined,
    fieldMode = false
): MobileDockConfig | null {
    if (isTechnicianShellActive(role, fieldMode)) {
        const mapSettings = (item: NavItem) =>
            item.to === '/technician/settings' ? { ...item, to: '/admin/settings' } : item;
        const moreLinks =
            typeof role === 'string' && role.trim().toLowerCase() === 'admin' && fieldMode
                ? technicianMobileMoreLinks.map(mapSettings)
                : technicianMobileMoreLinks;
        return { tabs: technicianMobileDockTabs, moreLinks };
    }
    const r = typeof role === 'string' ? role.trim().toLowerCase() : '';
    if (r === 'admin' || pathname.startsWith('/admin')) {
        return { tabs: adminMobileDockTabs, moreLinks: adminMobileMoreLinks };
    }
    if (r === 'technician' || pathname.startsWith('/technician')) {
        return { tabs: technicianMobileDockTabs, moreLinks: technicianMobileMoreLinks };
    }
    return null;
}

const MOBILE_NAV_PREFIXES: Record<string, string[]> = {
    '/admin/dashboard': ['/admin/dashboard'],
    '/admin/clients': ['/admin/clients', '/admin/devices'],
    '/admin/work-orders': ['/admin/work-orders', '/admin/work-order'],
    '/admin/work-order-forms': ['/admin/work-order-forms'],
    '/admin/inspections': ['/admin/inspections'],
    '/admin/quotes': ['/admin/quotes'],
    '/admin/invoices': ['/admin/invoices', '/admin/invoice'],
    '/admin/deficiencies': ['/admin/deficiencies'],
    '/admin/settings': ['/admin/settings', '/admin/pdf-studio'],
    '/technician/dashboard': ['/technician/dashboard'],
    '/technician/JobList': ['/technician/JobList', '/technician/jobs'],
    '/technician/TimeSheet': ['/technician/TimeSheet'],
    '/technician/JobLog': ['/technician/JobLog'],
    '/technician/ExportReport': ['/technician/ExportReport'],
    '/technician/settings': ['/technician/settings']
};

/** Match dock tabs across related routes (e.g. /admin/work-order/new → Jobs). */
export function isMobileNavActive(pathname: string, to: string): boolean {
    const prefixes = MOBILE_NAV_PREFIXES[to];
    if (!prefixes) {
        return pathname === to || pathname.startsWith(`${to}/`);
    }
    if (to === '/admin/dashboard' || to === '/technician/dashboard') {
        return pathname === to;
    }
    return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}
